//BranchHeadTickets.js

import * as React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import MUIDataTable, {
  MUIDataTableColumn,
  MUIDataTableOptions,
} from "mui-datatables";
import { createTheme, ThemeProvider, Theme } from "@mui/material/styles";
import {
  Box,
  Button,
  Card,
  IconButton,
  Tooltip,
  Grid,
  FormControl,
  Autocomplete,
  TextField,
} from "@mui/material";
import { Edit as EditIcon } from "@mui/icons-material";
import axios from "../../api";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import SoftBox from "components/SoftBox";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useSelector } from "react-redux";
import { useSnackbar } from "components/AlertMessages/SnackbarContext";
import RatingComponent from "../Ticket/Rating";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SoftTypography from "components/SoftTypography";
import { RootState } from "store";
import SlideOverPanel from "components/SlideOverPanel";
import BranchHeadAddTicket from "./BranchHeadAddTicket";
import EditTicket from "../Ticket/EditTicket";

const getMuiTheme = (theme: Theme): Theme =>
  createTheme({
    components: {
      MUIDataTable: {
        styleOverrides: {
          responsiveScroll: {
            maxHeight: "unset",
            overflowX: "auto",
          },
        },
      },
    },
  });

interface TicketStatus {
  status_id: number;
  status_name: string;
}

// Extracted cell components
interface DescriptionCellProps {
  value: string;
}

const DescriptionCell: React.FC<DescriptionCellProps> = ({ value }) => {
  const displayValue =
    value?.length > 20 ? `${value.substring(0, 20)}...` : value;
  return (
    <Tooltip title={value}>
      <span>{displayValue}</span>
    </Tooltip>
  );
};

interface AddressCellProps {
  value: string;
  city: string;
  state: string;
  addressType: string;
}

const AddressCell: React.FC<AddressCellProps> = ({
  value,
  city,
  state,
  addressType,
}) => {
  const addressData = `${value}, ${city}, ${state}, ${addressType}`;
  const displayValue =
    addressData?.length > 20
      ? `${addressData.substring(0, 20)}...`
      : addressData;

  return (
    <Tooltip title={addressData}>
      <span>{displayValue}</span>
    </Tooltip>
  );
};

interface ActionsCellProps {
  ticketData: any;
  onView: (ticketId: number) => void;
  onEscalate: (ticketId: number) => void;
  onEdit: (ticketId: number) => void;
  onCancel: (ticketId: number) => void;
  onRating: (data: any) => void;
}

const ActionsCell: React.FC<ActionsCellProps> = ({
  ticketData,
  onView,
  onEscalate,
  onEdit,
  onCancel,
  onRating,
}) => {
  return (
    <>
      <IconButton
        className="mt-eye-btn"
        aria-label="view"
        onClick={() => onView(ticketData.ticket_id)}
      >
        <VisibilityIcon />
      </IconButton>

      {[3, 4, 5].includes(ticketData?.status_id) &&
      !ticketData?.is_escalated ? (
        <Button
          sx={{ position: "relative" }}
          variant="contained"
          size="small"
          onClick={() => onEscalate(ticketData?.ticket_id)}
        >
          Escalation
        </Button>
      ) : null}

      {ticketData?.assigned_employee_id == null ? (
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <IconButton
            aria-label="edit"
            onClick={() => onEdit(ticketData?.ticket_id)}
          >
            <EditIcon />
          </IconButton>
          {ticketData?.status_id !== 9 && (
            <Button
              sx={{ position: "relative" }}
              variant="contained"
              size="small"
              onClick={() => onCancel(ticketData?.ticket_id)}
            >
              Cancel
            </Button>
          )}
        </Box>
      ) : (
        ticketData?.status_id === 6 && (
          <Button
            sx={{ position: "relative" }}
            variant="contained"
            size="small"
            onClick={() => onRating(ticketData)}
          >
            Rating
          </Button>
        )
      )}
    </>
  );
};

const BranchHeadTickets = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const urlStatusId = queryParams.get("status_id");
  React.useEffect(() => {
    if (urlStatusId) {
      setStatusFilter(Number(urlStatusId));
    }
  }, [urlStatusId]);

  const { fetchError, fetchSuccess } = useSnackbar();
  const navigate = useNavigate();
  const [rows, setRows] = React.useState<
    { is_escalated: boolean; [key: string]: any }[]
  >([]);
  const user = useSelector((state: RootState) => state.auth.user);
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [ticketData, setTicketData] = React.useState(null);
  const [statusFilter, setStatusFilter] = React.useState<number | null>(null);
  const [ticketStatuses, setTicketStatuses] = React.useState<TicketStatus[]>(
    []
  );
  const [closedStatusId, setClosedStatusId] = React.useState(null);
  const [addPanelOpen, setAddPanelOpen] = React.useState(false);
  const [editPanelOpen, setEditPanelOpen] = React.useState(false);
  const [selectedTicketId, setSelectedTicketId] = React.useState<number | null>(null);
  const tableOptions: MUIDataTableOptions = {
    selectableRows: "none",
    selectableRowsHeader: false,
    elevation: 0,
    tableBodyHeight: "auto",
    responsive: "standard",
    setRowProps: (row, dataIndex) => {
      const isEscalated = rows[dataIndex]?.is_escalated;
      return {
        style: {
          backgroundColor: isEscalated ? "#ffaba7" : undefined,
        },
      };
    },
  };
  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    };
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("en-US", options).format(date);
  };

  const handleTicketCancel = async (ticket_id: number) => {
    const ticketData = {
      status_id: 9,
    };
    try {
      await axios.put(`/tickets/${ticket_id}`, { ticketData });
      fetchData();
      fetchSuccess("Ticket Cancelled Successfully");
    } catch (err) {
      console.error("Error: ", err);
      fetchError("Unable to Cancel Ticket");
    }
  };

  const handleEscalationUpdate = async (ticket_id: number) => {
    const ticketData = {
      is_escalated: 1,
    };
    try {
      await axios.put(`/tickets/escalation/${ticket_id}`, ticketData);
      fetchData();
      fetchSuccess("Ticket escalated Successfully");
    } catch (err) {
      console.error("Error: ", err);
      fetchError("Unable to escalate Ticket");
    }
  };

  const handleRatingOpen = (data: React.SetStateAction<null>) => {
    setTicketData(data);
    setIsModalOpen(true);
  };

  const handleRatingClose = () => {
    setIsModalOpen(false);
  };

  const handleView = (ticketId: number) => {
    navigate(`/customer/ticket-view/${ticketId}`);
  };

  const handleEdit = (ticketId: number) => {
    setSelectedTicketId(ticketId);
    setEditPanelOpen(true);
  };

  const fetchStatuses = async () => {
    try {
      const response = await axios.get("/ticket-statuses");
      setTicketStatuses(response.data);

      const closedStatus = response.data.find(
        (status: { status_name: string }) =>
          status.status_name.toLowerCase() === "closed"
      );
      if (closedStatus) {
        setClosedStatusId(closedStatus.status_id);
      }
    } catch (error) {
      console.error("Error fetching statuses:", error);
    }
  };

  const fetchData = React.useCallback(async () => {
    try {
      const response = await axios.get(`/tickets/branch-head/${user?.userId}`);
      const allTickets = response?.data?.list || [];

      let filteredTickets = allTickets;
      if (!statusFilter && closedStatusId) {
        filteredTickets = allTickets.filter(
          (ticket: { status_id: number }) => ticket.status_id !== closedStatusId
        );
      } else if (statusFilter) {
        filteredTickets = allTickets.filter(
          (ticket: { status_id: number }) => ticket.status_id === statusFilter
        );
      }

      setRows(filteredTickets);
    } catch (error) {
      console.error(error);
      setRows([]);
    }
  }, [user?.userId, statusFilter, closedStatusId]);

  React.useEffect(() => {
    fetchStatuses();
  }, []);

  React.useEffect(() => {
    if (closedStatusId !== null) {
      fetchData();
    }
  }, [fetchData, closedStatusId]);

  // Fixed: Render functions that use the component instead of inline JSX
  const renderDescriptionCell = (value: string) => {
    return <DescriptionCell value={value} />;
  };

  const renderAddressCell = (value: string, tableMeta: any) => {
    const { currentTableData, rowIndex } = tableMeta;
    const data = tableMeta?.tableData[currentTableData[rowIndex]?.index];
    return (
      <AddressCell
        value={value}
        city={data?.city_name}
        state={data?.state_name}
        addressType={data?.address_type}
      />
    );
  };

  const renderActionsCell = (value: any, tableMeta: any) => {
    const { currentTableData, rowIndex } = tableMeta;
    const ticketData = tableMeta?.tableData[currentTableData[rowIndex]?.index];
    return (
      <ActionsCell
        ticketData={ticketData}
        onView={handleView}
        onEscalate={handleEscalationUpdate}
        onEdit={handleEdit}
        onCancel={handleTicketCancel}
        onRating={handleRatingOpen}
      />
    );
  };

  const columns: MUIDataTableColumn[] = [
    { name: "division_name", label: "Division" },
    { name: "title", label: "Title" },
    {
      name: "asset_name",
      label: "Brand Name",
    },
    {
      name: "description",
      label: "Description",
      options: {
        filter: false,
        customBodyRender: renderDescriptionCell,
      },
    },
    {
      name: "address",
      label: "Address",
      options: {
        filter: false,
        customBodyRender: renderAddressCell,
      },
    },
    { name: "employee_name", label: "Assignee" },
    {
      name: "created_at",
      label: "Created At",
      options: {
        filter: false,
        customBodyRender: (value) => formatDate(value),
      },
    },
    { name: "status_name", label: "Status" },
    {
      name: "id",
      label: "Actions",
      options: {
        filter: false,
        sort: false,
        customBodyRender: renderActionsCell,
      },
    },
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Card
        style={{ padding: "24px", margin: "16px auto", maxWidth: "2000px" }}
      >
        <Grid
          container
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
        >
          <Grid item xs={12} sm={6}>
            <FormControl>
              <SoftTypography
                variant="caption"
                fontWeight="bold"
                sx={{ pl: 0.5 }}
              >
                Status Filter
              </SoftTypography>
              <Autocomplete
                options={ticketStatuses}
                getOptionLabel={(option) => option.status_name}
                onChange={(event, value) => {
                  setStatusFilter(value?.status_id || null);
                }}
                renderInput={(params) => (
                  <TextField {...params} variant="outlined" />
                )}
                isOptionEqualToValue={(option, value) =>
                  option.status_id === value.status_id
                }
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setAddPanelOpen(true)}
            >
              Add Ticket
            </Button>
          </Grid>
        </Grid>

        <SoftBox
          sx={{
            "& .MuiTableRow-root:not(:last-child)": {
              "& td": (theme: Theme) => ({
                borderBottom: `${theme.borders.borderWidth[1]} solid ${theme.borders.borderColor}`,
              }),
            },
          }}
          className="manage-tickets"
        >
          <ThemeProvider theme={getMuiTheme}>
            <MUIDataTable
              title={"Manage Tickets"}
              data={rows}
              columns={columns}
              options={tableOptions}
            />
          </ThemeProvider>
        </SoftBox>
      </Card>
      {isModalOpen && (
        <RatingComponent
          onClose={handleRatingClose}
          isOpen={isModalOpen}
          ratingData={ticketData}
          fetchData={fetchData}
        />
      )}
      <SlideOverPanel
        open={addPanelOpen}
        onClose={() => setAddPanelOpen(false)}
        title="Add Ticket"
        width={1000}
      >
        <BranchHeadAddTicket
          embedded
          onSuccess={() => {
            setAddPanelOpen(false);
            fetchData();
          }}
          onCancel={() => setAddPanelOpen(false)}
        />
      </SlideOverPanel>
      <SlideOverPanel
        open={editPanelOpen}
        onClose={() => {
          setEditPanelOpen(false);
          setSelectedTicketId(null);
        }}
        title="Edit Ticket"
        width={1000}
      >
        {selectedTicketId != null && (
          <EditTicket
            id={String(selectedTicketId)}
            embedded
            onSuccess={() => {
              setEditPanelOpen(false);
              setSelectedTicketId(null);
              fetchData();
            }}
            onCancel={() => {
              setEditPanelOpen(false);
              setSelectedTicketId(null);
            }}
          />
        )}
      </SlideOverPanel>
    </DashboardLayout>
  );
};

export default BranchHeadTickets;
